'use client';

import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AuthLeftPanel from './AuthLeftPanel';
import LanguageSelectorScreen from './LanguageSelectorScreen';
import { useLanguage } from '@/contexts/LanguageContext';

export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const { hasChosenLanguage } = useLanguage();
  const [showAuth, setShowAuth] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Mark as mounted first — keeps server and client initial render identical
  useEffect(() => {
    setMounted(true);
  }, []);

  // Once we know whether the user has chosen a language, decide what to show
  useEffect(() => {
    if (!mounted) return;
    if (hasChosenLanguage) {
      setShowAuth(true);
    }
  }, [hasChosenLanguage, mounted]);

  // Before mount, render nothing (avoids server/client mismatch on hasChosenLanguage)
  if (!mounted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f5f0e8' }} />
    );
  }

  // Show language selector if user hasn't chosen yet
  if (!showAuth) {
    return (
      <LanguageSelectorScreen
        onLanguageSelected={() => setShowAuth(true)}
      />
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#f5f0e8' }}>
      {/* Left panel — brand */}
      <AuthLeftPanel />

      {/* Right panel — auth form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:px-16 xl:px-24">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg" style={{ backgroundColor: '#0f0f0f', color: '#f0e840' }}>
              ⚽
            </div>
            <span className="font-black text-lg tracking-tight" style={{ color: '#0f0f0f' }}>
              Fanxiety <span style={{ color: '#d4c800' }}>Club</span>
            </span>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#d4c800' }}>
              {activeTab === 'login' ? '😭 Welcome back, sufferer' : '⚽ Join the chaos'}
            </p>
            <h1 className="text-3xl font-black tracking-tight mb-2" style={{ color: '#0f0f0f' }}>
              {activeTab === 'login' ? 'Sign In to the Club' : 'Become a Member'}
            </h1>
            <p className="text-sm leading-relaxed" style={{ color: '#5a5040' }}>
              {activeTab === 'login' ? 'Your panic stats, quiz scores, and emotional damage are waiting.' : 'Create your account and start your journey of beautiful suffering.'}
            </p>
          </div>

          {/* Tab switcher */}
          <div className="flex p-1 rounded-xl mb-8" style={{ backgroundColor: '#e8e0d0' }}>
            {(['login', 'signup'] as const).map(tab => (
              <button
                key={`auth-tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                suppressHydrationWarning
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all duration-200`}
                style={
                  activeTab === tab
                    ? { backgroundColor: '#0f0f0f', color: '#f0e840' }
                    : { color: '#5a5040' }
                }
              >
                {tab === 'login' ? 'Sign In' : 'Join Club'}
              </button>
            ))}
          </div>

          {/* Form */}
          {activeTab === 'login' ? (
            <LoginForm onSwitchToSignup={() => setActiveTab('signup')} />
          ) : (
            <SignUpForm onSwitchToLogin={() => setActiveTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}